import * as React from "react";
import { useNotify } from "react-admin";
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack,
  CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell, IconButton,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DeleteIcon from "@mui/icons-material/Delete";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";

const ProBankInfo = () => {
  const [proId, setProId] = React.useState("");
  const [info, setInfo] = React.useState([]);
  const [iban, setIban] = React.useState("");
  const [bic, setBic] = React.useState("");
  const [holder, setHolder] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const notify = useNotify();

  const fetch = async () => {
    if (!proId.trim()) return;
    setLoading(true);
    try {
      const { json } = await httpClient(
        `${API_BASE_URL}/pro/${encodeURIComponent(proId)}/bank-informations`
      );
      setInfo(Array.isArray(json) ? json : json ? [json] : []);
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
      setInfo([]);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!proId.trim() || !iban || !holder) return;
    try {
      await httpClient(`${API_BASE_URL}/pro/${encodeURIComponent(proId)}/bank-information`, {
        method: "PUT",
        // Le backend attend une liste de BankInformationDTO
        // { id?, iban, bic, bankName, accountLabel } ; sans id = ajout.
        body: JSON.stringify([{ iban, bic, accountLabel: holder }]),
      });
      notify("Coordonnées bancaires mises à jour", { type: "success" });
      setIban(""); setBic(""); setHolder("");
      fetch();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  const remove = async (id) => {
    try {
      await httpClient(
        `${API_BASE_URL}/pro/${encodeURIComponent(proId)}/bank-informations/${encodeURIComponent(id)}`,
        { method: "DELETE" }
      );
      notify("RIB supprimé", { type: "success" });
      fetch();
    } catch (e) {
      notify(`Erreur : ${e.message}`, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AccountBalanceIcon /> Coordonnées bancaires pro
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Données sensibles. Ne pas partager. Modifications visibles immédiatement.
      </Alert>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField size="small" label="ID Pro" value={proId}
                onChange={(e) => setProId(e.target.value)} />
              <Button variant="contained" onClick={fetch} disabled={loading || !proId.trim()}>
                Charger
              </Button>
            </Box>
            <TextField size="small" label="Libellé du compte (titulaire)" value={holder}
              onChange={(e) => setHolder(e.target.value)} />
            <TextField size="small" label="IBAN" value={iban}
              onChange={(e) => setIban(e.target.value)} />
            <TextField size="small" label="BIC" value={bic}
              onChange={(e) => setBic(e.target.value)} />
            <Box>
              <Button variant="outlined" onClick={save}
                disabled={!proId.trim() || !iban || !holder}>
                Enregistrer
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>RIB existants</Typography>
          {loading ? <CircularProgress size={20} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Libellé</TableCell>
                  <TableCell>Banque</TableCell>
                  <TableCell>IBAN</TableCell>
                  <TableCell>BIC</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {info.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">Aucun RIB</TableCell></TableRow>
                ) : info.map((b, i) => (
                  <TableRow key={b.id || i}>
                    <TableCell>{b.id}</TableCell>
                    <TableCell>{b.accountLabel || "—"}</TableCell>
                    <TableCell>{b.bankName || "—"}</TableCell>
                    <TableCell>{b.iban}</TableCell>
                    <TableCell>{b.bic}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => remove(b.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default { list: ProBankInfo };
